import React from 'react';
import type { useChatSession } from '../hooks/useChatSession';
import { useFileUpload } from '../hooks/useFileUpload';
import type { DataResponse } from '../types/dataResponse';
import FileUpload, { AttachedFilesList, UploadStatus } from './FileUpload';

const suggestions = [
  "Let's optimize the production of biodiesel in my CSTR reactor. The reaction that is carried out is trans-esterification",
  "Let's optimize the flowsheet attached with my historian data you can find attached",
  "Let's draw this flowsheet for my reactor system. I have two inlets, a reactor and an outlet. I measure TT-101 in the reactor. Draw the flowsheet",
];

interface StartPageProps {
  chatSession: ReturnType<typeof useChatSession>;
  onPendingMessageChange: (content: string, attachments?: any[]) => void;
  onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  onResponse?: (data: DataResponse[]) => void;
}

const StartPage: React.FC<StartPageProps> = ({
  chatSession,
  onPendingMessageChange,
  onSubmit,
  onResponse,
}) => {
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = React.useRef<HTMLDivElement>(null);
  
  // Get sessionId and userID from chatSession
  const { sessionId, userID } = chatSession;
  
  // Typewriter animation states
  const [currentText, setCurrentText] = React.useState('');
  const [currentPhraseIndex, setCurrentPhraseIndex] = React.useState(0);
  const [isDeleting, setIsDeleting] = React.useState(false);
  
  const phrases = [
    'your process?',
    'your reactive system?',
    'your spray dryer?',
    'optimizing your production?',
    'decreasing your emissions?'
  ];
  
  const {
    attachedFiles,
    isUploading,
    handleFilesSelected,
    handleRemoveFile,
    uploadFiles,
    clearFiles
  } = useFileUpload({ sessionId, userID });

  // Enhanced file selection handler that maintains focus and cursor position
  const handleFilesSelectedWithFocus = (files: File[]) => {
    // Store current cursor position before file selection
    const currentPosition = textareaRef.current?.selectionStart || 0;
    
    // Handle file selection
    handleFilesSelected(files);
    
    // Restore focus and cursor position after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(currentPosition, currentPosition);
      }
    }, 0);
  };

  // Enhanced file removal handler that maintains focus and cursor position
  const handleRemoveFileWithFocus = (fileName: string) => {
    // Store current cursor position before file removal
    const currentPosition = textareaRef.current?.selectionStart || 0;
    
    // Handle file removal
    handleRemoveFile(fileName);
    
    // Restore focus and cursor position after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(currentPosition, currentPosition);
      }
    }, 0);
  };

  // Handle clicks outside the chat bar (form) to remove focus and hide suggestions
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const form = containerRef.current?.querySelector('form');
      const suggestions = suggestionsRef.current;
      
      // Check if click is outside both form and suggestions
      const isOutsideForm = form && !form.contains(event.target as Node);
      const isOutsideSuggestions = suggestions && !suggestions.contains(event.target as Node);
      
      if (isOutsideForm && isOutsideSuggestions) {
        // Remove focus from textarea to hide cursor
        if (textareaRef.current && document.activeElement === textareaRef.current) {
          textareaRef.current.blur();
        }
        // Hide suggestions
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Typewriter animation effect
  React.useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    const typingSpeed = 50; // ms per character
    const deletingSpeed = 40; // ms per character when deleting
    const pauseTime = 2000; // ms to pause at end of phrase

    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentPhrase.length) {
          setCurrentText(currentPhrase.slice(0, currentText.length + 1));
        } else {
          // Finished typing, pause then start deleting
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          // Finished deleting, move to next phrase
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timer);
  }, [currentText, currentPhraseIndex, isDeleting, phrases]);

  // Custom suggestion click handler
  const handleSuggestionClick = (suggestion: string) => {
    // Update the input value
    setInputValue(suggestion);
    onPendingMessageChange(suggestion, attachedFiles);
    
    // Keep suggestions visible and focus the textarea
    setShowSuggestions(true);
    
    // Focus the textarea and position cursor at the end
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(suggestion.length, suggestion.length);
        // Auto-resize textarea after setting value
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        const lineHeight = 20;
        const padding = 0;
        const minHeight = lineHeight + padding;
        const maxHeight = (lineHeight * 5) + padding;
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
        textareaRef.current.style.height = `${newHeight}px`;
      }
    }, 0);
  };

  // Custom submit handler that uses chatSession's centralized logic
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    // Use the chatSession's centralized submit handler
    await chatSession.handleCustomSubmit(
      e, 
      inputValue,
      attachedFiles, 
      uploadFiles, 
      clearFiles, 
      onResponse
    );
    
    // Switch to chat view after submission
    onSubmit(e);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white select-none px-4 py-8">
      {/* Logo */}
      <div className="mb-6 mt-[-10vh]">
        <img 
          src="/logo_blue_full.webp" 
          alt="Logo" 
          className="h-auto w-full max-w-md"
          loading="eager"
          decoding="async"
          style={{ 
            objectFit: 'contain'
          }}
        />
      </div>
      
      {/* Subtitle text */}
      <div className="mb-8 text-center">
        <p className="text-lg md:text-xl text-gray-600 font-thin select-none" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          What can <span style={{ color: "#1e9cd7" }}>enablai</span> do for{' '}
          <span className="relative">
            {currentText}
          </span>
        </p>
      </div>

      <div className="relative w-full max-w-3xl" ref={containerRef}>
        <form onSubmit={handleFormSubmit} className="w-full space-y-3">
          <div className="rounded-md overflow-hidden shadow-md border bg-blue-100 flex items-center gap-2 px-4 py-3">
            <FileUpload onFilesSelected={handleFilesSelectedWithFocus} />
            <div className="flex-1">
              <div className="flex flex-col w-full">
                <AttachedFilesList 
                  attachedFiles={attachedFiles} 
                  onRemoveFile={handleRemoveFileWithFocus} 
                />
                <textarea
                  ref={textareaRef}
                  className="flex-1 bg-transparent outline-none text-sm text-gray-700 resize-none overflow-y-auto flex items-center select-text"
                  value={inputValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInputValue(newValue);
                    onPendingMessageChange(newValue, attachedFiles);
                    // Auto-resize textarea
                    const textarea = e.target;
                    textarea.style.height = 'auto';
                    const scrollHeight = textarea.scrollHeight;
                    const lineHeight = 20; // line height
                    const padding = 0; // account for padding
                    const minHeight = lineHeight + padding; // Single line height
                    const maxHeight = (lineHeight * 5) + padding; // 5 lines max
                    const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
                    textarea.style.height = `${newHeight}px`;
                  }}
                  onFocus={() => {
                    // Show suggestions when textarea is focused
                    setShowSuggestions(true);
                  }}
                  onClick={() => {
                    // Show suggestions when textarea is clicked
                    setShowSuggestions(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      handleFormSubmit(e as any);
                    }
                  }}
                  placeholder="Let's..."
                  disabled={isUploading}
                  rows={1}
                  style={{ 
                    minHeight: '28px', 
                    maxHeight: '108px', 
                    lineHeight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: '4px',
                    paddingBottom: '4px'
                  }}
                />
              </div>
            </div>
          </div>
          <UploadStatus isUploading={isUploading} />
        </form>

        {/* Conditionally visible suggestions with modern design */}
        {showSuggestions && (
          <div className="absolute top-full mt-3 w-full z-10 max-h-60 overflow-y-auto" ref={suggestionsRef}>
            <div className="flex flex-col gap-2">
              {suggestions.map((text, i) => (
                <div
                  key={i}
                  className="bg-white border border-blue-300 rounded-lg px-3 py-2 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all duration-200 ease-in-out"
                  onClick={() => handleSuggestionClick(text)}
                >
                  <p className="text-xs md:text-sm text-gray-700">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartPage; 